import { useQuery } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { PRICE_MAX, WEIGHT_MAX, type WasteFilters } from "@/hooks/use-waste-filters";
import type { BoundingArea } from "@/lib/image-crop";
import { useTranslation } from "@/i18n/use-translation";
import { apiError } from "@/lib/api-error";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

export type WasteEvent = {
  id: string;
  org_id: string;
  site_id: string;
  kitchen_id: string;
  kitchen_name: string | null;
  device_serial: string | null;
  // The capturing device's crop box, joined from `devices` (POS2-1105). Null when
  // the device crops on-device, which renders the capture as-is.
  bounding_area: BoundingArea | null;
  image_url: string | null;
  before_image_url: string | null;
  image_read_url: string | null;
  before_image_read_url: string | null;
  item_weight: number;
  raw_weight: number | null;
  logged_at: string;
  processed_at: string | null;
  status: "processing" | "successful" | "discarded";
  source: "device" | "manual" | "imported";
  classification_level: number | null;
  category_id: string | null;
  subcategory_id: string | null;
  inventory_item_id: string | null;
  recipe_id: string | null;
  waste_type: string | null;
  service_time: string | null;
  cost_estimate: number | null;
  cost_method: string | null;
  ai_reasoning: string | null;
  is_confirmed: boolean;
  confirmed_by: string | null;
  confirmed_at: string | null;
  created_at: string;
  updated_at: string;
  category_name: string | null;
  subcategory_name: string | null;
  inventory_item_name: string | null;
  timezone: string | null;
  currency: string;

  item: { id: string; label: string } | null;
  weight_plausible: boolean | null;
  weight_override: boolean;
  surface_item: { id: string; label: string } | null;
  bulk_assessment: "unidentified" | "same_as_surface" | "different_identified" | null;
  delta_description: string | null;
  l1_diagnostic: { id: string; description: string; action: string | null } | null;

  boosted_suggestion: { id: string; label: string } | null;
  menu_matches: { inventory_item_id: string; description: string; recipe_ids: string[] }[] | null;
  menu_boosted: boolean;
  history_boosted: boolean;

  comment_count: number;
};

type WasteEventsResult = {
  events: WasteEvent[];
  total: number;
};

export function useWasteEvents(
  filters: WasteFilters,
  dateRange: { from: string | null; to: string | null },
) {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const params = buildQueryParams(filters, dateRange);

  return useQuery<WasteEventsResult>({
    queryKey: ["waste-events", params.toString()],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/waste/events?${params.toString()}`, {
        headers,
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      const body = (await res.json()) as { data: WasteEvent[] };
      const total = Number.parseInt(res.headers.get("X-Total-Count") ?? "0", 10) || 0;
      return { events: body.data, total };
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
    placeholderData: (prev) => prev,
  });
}

/** Fetch a single event by id — used to open a deep-linked comment thread for an
 * event that isn't on the current Transactions page/filter (POS2-761). */
export function useWasteEvent(eventId: string | null) {
  const { t } = useTranslation();
  const headers = useApiHeaders();

  return useQuery<WasteEvent>({
    queryKey: ["waste-event", eventId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/waste/events/${eventId}`, { headers });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      return ((await res.json()) as { data: WasteEvent }).data;
    },
    enabled: !!eventId && !!headers.Authorization.replace("Bearer ", ""),
  });
}

export function buildQueryParams(
  filters: WasteFilters,
  dateRange: { from: string | null; to: string | null },
): URLSearchParams {
  const params = new URLSearchParams();
  if (dateRange.from) params.set("date_from", dateRange.from);
  if (dateRange.to) params.set("date_to", dateRange.to);
  if (filters.site_id) params.set("site_id", filters.site_id);
  if (filters.kitchen_id) params.set("kitchen_id", filters.kitchen_id);
  if (filters.device_serial) params.set("device_serial", filters.device_serial);
  if (filters.waste_type) params.set("waste_type", filters.waste_type);
  if (filters.classification_level != null) {
    params.set("classification_level", String(filters.classification_level));
  }
  if (filters.status) params.set("status", filters.status);
  if (filters.service_time) params.set("service_time", filters.service_time);
  if (filters.is_confirmed != null) params.set("is_confirmed", String(filters.is_confirmed));
  // POS2-758: Food maps to the identified inventory item. Price/weight ranges
  // are sent only when narrowed off their defaults — sending price_min=0 would
  // filter cost_estimate >= 0 and wrongly drop null-cost (unclassified) events.
  if (filters.food) params.set("inventory_item_id", filters.food);
  if (filters.price_min > 0) params.set("price_min", String(filters.price_min));
  if (filters.price_max < PRICE_MAX) params.set("price_max", String(filters.price_max));
  if (filters.weight_min > 0) params.set("weight_min", String(filters.weight_min));
  if (filters.weight_max < WEIGHT_MAX) params.set("weight_max", String(filters.weight_max));
  // Local "HH:MM" time-of-day window, applied within each day of the date range
  // (the backend converts to UTC using the scope's timezone).
  if (filters.time_from) params.set("time_from", filters.time_from);
  if (filters.time_to) params.set("time_to", filters.time_to);
  params.set("sort_by", filters.sort_by);
  params.set("sort_dir", filters.sort_dir);
  params.set("offset", String(filters.page * filters.page_size));
  params.set("limit", String(filters.page_size));
  return params;
}
